import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchAdminCompetition, deleteCompetitionEvent, deleteCompetition } from '../../api/admin/api';
import { format } from 'date-fns';
import Header from '../../components/Header';
import Stack from '../../components/Stack';
import Card from '../../components/Card'
import Button from '../../components/Button'
import { FaTrash, FaEllipsisV, FaTrashAlt, FaEdit } from 'react-icons/fa';

export default function CompetitionDetail() {
  const { id } = useParams();
  const [competition, setCompetition] = useState(null);
  const navigate = useNavigate()

  useEffect(() => {
    fetchAdminCompetition(id).then((res) => setCompetition(res));
  }, [id]);

  const handleNav = (event_id) => {
    navigate(`/admin/competition_events/${event_id}`)
  }

  const deleteEvent = async (eventId) => {
    try {
      await deleteCompetitionEvent(eventId);
      setCompetition((prev) => ({
        ...prev,
        competition_events: prev.competition_events.filter((e) => e.id !== eventId),
      }));
    } catch (err) {
      console.error(err);
      alert('Failed to delete competition event');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCompetition(id);
      navigate('/admin/competitions')
    } catch (err) {
      console.error(err);
      alert('Failed to delete competition');
    }
  };

  if (!competition) return <div className="p-8">Loading...</div>;

  const {
    name,
    team_size,
    rookie_count,
    veteran_count,
    event_participation_limit,
    include_address,
    office_restriction,
    competition_events = [],
    vivint_competition_id
  } = competition;

  const firstEvent = competition_events[0];
  const startDate = firstEvent?.start_date
    ? format(new Date(firstEvent.start_date), 'MM/dd/yyyy')
    : 'TBD';
  const endDate = firstEvent?.end_date
    ? format(new Date(firstEvent.end_date), 'MM/dd/yyyy')
    : 'TBD';

  return (
    <Card className='gap-8'>
      <Stack direction='row' justify='between'>
        <Header size="xl">{name}</Header>
        <button
          onClick={() => {
            if (window.confirm('Are you sure you want to delete this competition?')) {
              handleDelete(id);
            }
          }}
          className="text-xs text-red-600 hover:underline font-medium flex gap-2"
        >
          <FaTrash className="text-sm" />
          Delete Competition
        </button>
      </Stack>

      <Link to={`/admin/competitions/editing/${true}/${id}`} className='w-11/12 border-b-2 border-gray-400 pb-6'>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-700">
          <div><strong>Vivint Competition ID:</strong> {vivint_competition_id}</div>
          <div><strong>Team Size:</strong> {team_size}</div>
          <div><strong>Rookies:</strong> {rookie_count ?? 0}</div>
          <div><strong>Vets:</strong> {veteran_count ?? 0}</div>
          <div><strong>Include Address:</strong> {include_address ? 'Yes' : 'No'}</div>
          <div><strong>Office Restriction:</strong> {office_restriction ? 'Yes' : 'No'}</div>
          <div><strong>Event Participation Limit:</strong> {event_participation_limit ? 'Yes' : 'No'}</div>
          <div><strong>Start Date:</strong> {startDate}</div>
          <div><strong>End Date:</strong> {endDate}</div>
          <div><strong>Total Events:</strong> {competition_events.length}</div>
        </div>
      </Link>

      <Stack width="full" direction='col' justify='between'>
        <Stack width="11/12" direction='row' className='py-4 border-b border-gray-500 mb-4'>
          <Stack className='w-auto gap-1' align='start'>
            <Header size="xl">Competition Events</Header>
            <p className='text-sm'>All occurrences of a competition are listed below.</p>
          </Stack>
          <Button
            className="py-2 px-4 text-xs"
            onClickMethod={() => navigate(`/admin/competitions/${id}/competition_events/editing/${false}/null`)}
          >
            Create New
          </Button>
        </Stack>

        <Stack className="gap-4 flex-wrap" direction='row' justify='start' align='start'>
          {competition_events.sort((a,b) => format(new Date(a.start_date), 'yyyyMMdd') - format(new Date(b.start_date), 'yyyyMMdd')).map((event) => {
            return (
              <Stack
                key={event.id}
                direction='row'
                justify='between'
                className='w-auto gap-6 border shadow-md rounded-md p-4 relative'
              >
                <div onClick={() => handleNav(event.id)}>
                  <p className="text-medium text-black"><strong>{event.name}</strong></p>
                  <p className="text-medium text-black"><strong>Event Date:</strong> {event.start_date} - {event.end_date}</p>
                  <p className="font-xs text-gray-600">Event ID: {event.id}</p>
                  <p className="text-xs text-gray-600">{event.team_count ?? 0} Teams Registered</p>
                </div>

                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompetition((prev) => ({
                        ...prev,
                        competition_events: prev.competition_events.map((ev) =>
                          ev.id === event.id ? { ...ev, showMenu: !ev.showMenu } : { ...ev, showMenu: false }
                        ),
                      }));
                    }}
                    className="text-gray-600 hover:text-black"
                  >
                    <FaEllipsisV />
                  </button>

                  {event.showMenu && (
                    <div className="absolute right-0 top-6 w-auto min-w-max bg-white border border-gray-200 rounded shadow-md z-10 text-sm">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Are you sure you want to delete this event?')) {
                            deleteEvent(event.id);
                          }
                        }}
                        className="block px-4 py-2 text-xs text-red-600 hover:bg-gray-100 w-full text-left flex gap-2 items-center pr-4"
                      >
                        <FaTrashAlt className="text-red-500 text-base" />
                        Delete Event
                      </button>
                      <button
                        onClick={() => {
                          navigate(`/admin/competitions/${id}/competition_events/editing/${true}/${event.id}`);
                        }}
                        className="block px-4 py-2 text-xs text-black-600 hover:bg-gray-100 w-full text-left flex gap-2 items-center pr-4"
                      >
                        <FaEdit className="text-gray-700 text-base" />
                        Edit Event
                      </button>
                    </div>
                  )}
                </div>
              </Stack>
              );
            })}
          </Stack>
        </Stack>
    </Card>
  );
}

